import React from 'react';
import { useLocation } from 'react-router-dom';
import { readCookiePreferences } from '../utils/cookiePreferences';
import { useAuth } from '../context/AuthContext';
import { applyContentLocaleToUrl } from '../utils/contentLocaleQuery';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const VISITOR_KEY = 'zm_vid';

function getOrCreateVisitorId(): string {
  try {
    const existing = window.localStorage.getItem(VISITOR_KEY);
    if (existing && existing.length >= 8) return existing;
    const id = crypto.randomUUID();
    window.localStorage.setItem(VISITOR_KEY, id);
    return id;
  } catch {
    return `anon_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }
}

/**
 * Sends anonymized page-view beacons when analytics cookies are allowed.
 * Hook is safe to mount once under the storefront shell.
 */
export function useStorefrontTraffic() {
  const location = useLocation();
  const { auth } = useAuth();
  const [analyticsOn, setAnalyticsOn] = React.useState(() => readCookiePreferences()?.analytics ?? false);

  React.useEffect(() => {
    const onPrefs = () => setAnalyticsOn(Boolean(readCookiePreferences()?.analytics));
    window.addEventListener('lewouhmart:cookie-preferences-updated', onPrefs as EventListener);
    return () => window.removeEventListener('lewouhmart:cookie-preferences-updated', onPrefs as EventListener);
  }, []);

  React.useEffect(() => {
    if (!analyticsOn) return;

    const path = `${location.pathname}${location.search}`.slice(0, 2048);
    const visitorId = getOrCreateVisitorId();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (auth.accessToken) {
      headers.Authorization = `Bearer ${auth.accessToken}`;
    }

    const body = JSON.stringify({
      visitorId,
      path,
      kind: 'PAGE_VIEW',
    });

    void fetch(
      applyContentLocaleToUrl(
        `${API_BASE}/public/storefront-traffic/events`,
        navigator.language,
      ),
      {
        method: 'POST',
        headers,
        body,
        credentials: 'omit',
      },
    ).catch(() => {
      /* ignore */
    });
  }, [analyticsOn, location.pathname, location.search, auth.accessToken]);
}
